import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

// Dialogue "Ajouter au stock" depuis une fiche reprise : pré-remplit le
// formulaire de création d'article avec les informations du téléphone
// racheté —
//   Offre de rachat -> Prix fournisseur (prix d'achat)
//   Nom = modèle + capacité + IMEI
//   TVA = 0 %, Quantité = 1, Référence article = IMEI
//   Catégorie = "Téléphone d'occasion"
// Tout reste modifiable avant validation : ce n'est qu'un point de départ,
// pas une création automatique silencieuse.
export function AjouterAuStockDialog({ open, onClose, reprise, onAjoute }) {
  const [categories, setCategories] = useState([]);
  const [nom, setNom] = useState("");
  const [code, setCode] = useState("");
  const [categorieId, setCategorieId] = useState("");
  const [quantite, setQuantite] = useState(1);
  const [prixAchat, setPrixAchat] = useState(0);
  const [prixTtc, setPrixTtc] = useState(0);
  const [tvaPercent, setTvaPercent] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data } = await api.get("/categories");
      setCategories(data);
      const occasion = data.find((c) => c.nom.toLowerCase() === "téléphone d'occasion");
      setCategorieId(occasion ? occasion.nom : (data[0]?.nom || ""));
    })();
    const nomDefaut = [reprise.modele, reprise.capacite, reprise.imei].filter(Boolean).join(" ");
    setNom(nomDefaut);
    setCode(reprise.imei || "");
    setQuantite(1);
    setPrixAchat(reprise.offre_rachat || 0);
    setPrixTtc(0);
    setTvaPercent(0);
  }, [open, reprise]);

  const submit = async () => {
    if (!nom.trim()) { toast.error("Le nom de l'article est obligatoire"); return; }
    setSubmitting(true);
    try {
      const { data: article } = await api.post("/stock", {
        nom: nom.trim(), code: code.trim() || undefined, categorie: categorieId,
        quantite: Number(quantite) || 0, prix_achat: Number(prixAchat) || 0,
        prix_ttc: Number(prixTtc) || 0, tva_percent: Number(tvaPercent) || 0,
      });
      await api.post(`/reprises/${reprise.id}/marquer-ajoute-stock`, { article_id: article.id });
      toast.success("Téléphone ajouté au stock");
      onAjoute(article);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ajouter-au-stock-dialog">
        <DialogHeader><DialogTitle>Ajouter ce téléphone au stock</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Les champs sont pré-remplis à partir de la fiche reprise — vérifiez-les et ajustez-les si besoin
          avant de valider.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Nom de l'article</label>
            <Input value={nom} onChange={(e) => setNom(e.target.value)} data-testid="ajouter-stock-nom" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Référence article (IMEI)</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="ajouter-stock-code" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Catégorie</label>
              <Select value={categorieId} onValueChange={setCategorieId}>
                <SelectTrigger data-testid="ajouter-stock-categorie"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.nom}>{c.nom}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Quantité</label>
              <Input type="number" min="1" value={quantite} onChange={(e) => setQuantite(e.target.value)} data-testid="ajouter-stock-quantite" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Prix fournisseur (achat)</label>
              <Input type="number" step="0.01" value={prixAchat} onChange={(e) => setPrixAchat(e.target.value)} data-testid="ajouter-stock-prix-achat" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">TVA (%)</label>
              <Input type="number" step="0.1" value={tvaPercent} onChange={(e) => setTvaPercent(e.target.value)} data-testid="ajouter-stock-tva" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              Prix de vente TTC (laisser à 0 pour un calcul automatique à partir du prix fournisseur)
            </label>
            <Input type="number" step="0.01" value={prixTtc} onChange={(e) => setPrixTtc(e.target.value)} data-testid="ajouter-stock-prix-ttc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} data-testid="ajouter-stock-confirmer">
            {submitting ? "Ajout..." : "Ajouter au stock"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
